import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// Share dialog — surfaces the partner-facing portal URL and the embed
// iframe snippet so the admin can hand off the form. Both URLs are owned
// by the SPA portal route + the embed route; this dialog just displays + copies.

// `VITE_APP_URL` is read at module-eval time so the value the user sees
// matches what the renderer ships. Falling back to empty string makes
// relative-URL copies obvious during local dev.
const APP_URL =
  typeof import.meta.env.VITE_APP_URL === "string"
    ? import.meta.env.VITE_APP_URL
    : "";

export function ShareFormDialog({
  open,
  onOpenChange,
  slug,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  slug: string;
}) {
  const portalUrl = `${APP_URL}/portal/forms/${slug}`;
  const embedSrc = `${APP_URL}/embed/forms/${slug}`;
  const embedSnippet = `<iframe src="${embedSrc}" width="100%" height="800" frameborder="0"></iframe>`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Share this form
          </DialogTitle>
          <DialogDescription>
            Use the portal URL for authenticated partners, or paste the embed
            snippet on any external page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="portal-url">Portal URL</Label>
            <CopyableInput id="portal-url" value={portalUrl} />
            <p className="text-[11px] text-muted-foreground">
              Authenticated partners see this URL inside the portal — works
              with their existing session.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="embed-snippet">Embed snippet</Label>
            <CopyableTextarea id="embed-snippet" value={embedSnippet} />
            <p className="text-[11px] text-muted-foreground">
              Paste this HTML on any external page. The embed renders without
              your portal chrome.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyableInput({ id, value }: { id: string; value: string }) {
  const [copied, setCopied] = useState(false);

  function onCopy() {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => toast.error("Copy failed"));
  }

  return (
    <div className="flex items-center gap-1.5">
      <Input
        id={id}
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="font-mono text-xs"
      />
      <Button
        type="button"
        size="icon"
        variant="outline"
        onClick={onCopy}
        aria-label="Copy"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-status-active" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
    </div>
  );
}

function CopyableTextarea({ id, value }: { id: string; value: string }) {
  const [copied, setCopied] = useState(false);

  function onCopy() {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => toast.error("Copy failed"));
  }

  return (
    <div className="flex items-start gap-1.5">
      <pre
        id={id}
        className="flex-1 overflow-x-auto rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs"
      >
        <code>{value}</code>
      </pre>
      <Button
        type="button"
        size="icon"
        variant="outline"
        onClick={onCopy}
        aria-label="Copy"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-status-active" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
    </div>
  );
}
